import { AfterViewInit, Component, Inject, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { VersioningService } from '@client-monorepo/versioning';
import { IntrackService } from '@client-monorepo/shared/common';
import { FeedbackSheetComponent } from './components/feedback-sheet/feedback-sheet.component';
import { feedBackParamsModel, OpenFeedbackInterface } from './data-access/models/openFeedback.interface';
import { ApiImageService } from '@digipay/ng-ui-api-image';
import { debounceTime, filter } from 'rxjs';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { AbTestService, LayoutService } from '@client-monorepo/common/utilities';
import { Title } from '@angular/platform-browser';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { environment } from '../environments/environment';

declare const window: OpenFeedbackInterface;

@Component({
  standalone: true,
  imports: [RouterModule, CommonModule],
  selector: 'dpx-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements AfterViewInit {
  title = 'dpx';
  isResizing = signal(false);
  private versioning = inject(VersioningService);
  private intrackService = inject(IntrackService);
  gtmService = inject(GoogleTagManagerService);
  private activatedRoute = inject(ActivatedRoute);
  bottomSheetService = inject(NgxBottomSheetService);
  apiImageService = inject(ApiImageService);
  router = inject(Router);
  private lastUrlAfterRedirect: string;
  private readonly regex = /[^?]*/;
  private titleService = inject(Title);
  layoutService = inject(LayoutService);

  constructor(@Inject('APP_ENV') private environment: { [key: string]: string }) {
    this.versioning.run();
    this.intrackService.initIntrack();
    this.setTagManagerConfig();
    this.apiImageService.prefixUrl = this.environment[AbTestService.callApiWithAbsoluteUrl() ? 'base_url_origin_abs' : 'base_url_origin'];
  }

  ngAfterViewInit() {
    this.subscribeToHorizontalResize();
    setTimeout(() => {
      document.getElementById('nativeSplash')?.remove();
      this.openFeedBackModal();
      this.setIntrackFeatures();
      this.gtmServiceRouterEvent();
    }, 3000);
    setTimeout(() => {
      document.getElementById('splash_bg')?.remove();
    }, 500);
    this.titleServiceRouterEvent();
  }
  private setIntrackFeatures(): void {
    this.intrackService.intrackOnSiteMessage();
    this.intrackService.intrackWebPush();
  }

  private setTagManagerConfig(): void {
    if (this.environment['google_tag_manager_id']) {
      this.gtmService.addGtmToDom();
    }
  }

  subscribeToHorizontalResize(): void {
    this.layoutService
      .onHorizontalResize()
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.isResizing.set(true);
        setTimeout(() => {
          this.isResizing.set(false);
        }, 10);
      });
  }

  private openFeedBackModal(): void {
    window.openFeedback = (params: feedBackParamsModel) => {
      this.bottomSheetService.openBottomSheet(FeedbackSheetComponent, { feedBackData: params }, { height: '70%' });
    };
  }

  gtmServiceRouterEvent() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.saveCustomQueryParam();
        // Prevent sending duplicate page events to GTM after changing the query parameters in the dropdown.
        if (environment.env === 'production') {
          if (this.lastUrlAfterRedirect?.match(this.regex)[0] !== this.router.url.match(this.regex)[0]) {
            this.gtmService.pushTag({
              event: 'page_event',
              page_url: event.urlAfterRedirects,
              old_page: this.lastUrlAfterRedirect ?? '/',
            });
            this.lastUrlAfterRedirect = this.router.url;
          }
        }
      }
    });
  }

  private titleServiceRouterEvent() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.titleService.setTitle('دیجی‌پی | خدمات مالی و پرداخت'); // Reset the title after navigation
      }
    });
  }

  private saveCustomQueryParam(): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { ...this.activatedRoute.snapshot.queryParams },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      fragment: this.activatedRoute.snapshot.fragment,
    });
  }
}
